/**
 * DocumentsWidget - Affichage des documents avec sélecteur de vue
 * 
 * Permet de basculer entre différents modes d'affichage :
 * - Grid : Grille compacte avec embed
 * - List : Liste avec avatar et métadonnées
 * - Card : Cartes détaillées avec média
 * - Timeline : Vue chronologique
 * - Masonry : Disposition en maçonnerie
 * - Embed : Embeds natifs des réseaux sociaux
 */

import React, { useState, useEffect } from "react";
import { Typography } from "@/components/ui";
import useTranslation from "@/hooks/use-translation";
import type { DocumentsData, DocumentsVisualConfig } from "@/models/widget-v2";
import type { Content } from "@/models/document";
import SearchViewSelector, { SearchViewMode } from "@/components/search-view-selector";
import {
  GridView,
  ListView,
  CardView,
  TimelineView,
  MasonryView,
  EmbedView,
} from "@/components/search-views";

// ============================================================================
// Types
// ============================================================================

interface DocumentsWidgetProps {
  data: DocumentsData;
  visual: DocumentsVisualConfig;
}

// ============================================================================
// Main Component
// ============================================================================

export const DocumentsWidget: React.FC<DocumentsWidgetProps> = ({
  data,
  visual,
}) => {
  const { t } = useTranslation();
  // Utiliser la vue par défaut depuis la config, sinon "card"
  const defaultView = visual.defaultView || "card";
  const [currentView, setCurrentView] = useState<SearchViewMode>(defaultView);

  // Mettre à jour la vue si la config change
  useEffect(() => {
    if (visual.defaultView) {
      setCurrentView(visual.defaultView);
    }
  }, [visual.defaultView]);

  // Sauvegarder la préférence de vue (optionnel - pour persister le choix utilisateur)
  const handleViewChange = (view: SearchViewMode) => {
    setCurrentView(view);
  };

  // Pas de documents
  if (!data.documents || data.documents.length === 0) {
    return (
      <div className="h-full w-full flex flex-col">
        {visual.title && (
          <div className="px-4 pt-4 pb-2">
            <Typography variant="h6" className="text-content-primary">
              {visual.title}
            </Typography>
          </div>
        )}
        <div className="flex-1 flex items-center justify-center text-content-tertiary">
          <Typography variant="text-sm">Aucun document à afficher</Typography>
        </div>
      </div>
    );
  }

  // Cast des documents vers Content[]
  const items = data.documents as unknown as Content[];

  // Rendu de la vue sélectionnée
  const renderView = () => {
    switch (currentView) {
      case "list":
        return <ListView items={items} />;
      case "card":
        return <CardView items={items} />;
      case "timeline":
        return <TimelineView items={items} />;
      case "masonry":
        return <MasonryView items={items} />;
      case "embed":
        return <EmbedView items={items} />;
      case "grid":
      default:
        return <GridView items={items} />;
    }
  };

  return (
    <div className="h-full w-full flex flex-col">
      {/* Header avec sélecteur de vue à gauche (pour éviter conflit avec menu widget) */}
      <div className="flex items-center gap-3 px-4 py-2 border-b border-arc-border bg-arc-surface-secondary shrink-0">
        <SearchViewSelector
          currentView={currentView}
          onViewChange={handleViewChange}
        />
        {visual.title && (
          <Typography variant="h6" className="text-content-primary">
            {visual.title}
          </Typography>
        )}
        <Typography variant="text-xs" className="text-content-tertiary">
          ({t("n_documents", { count: data.total })})
        </Typography>
      </div>

      {/* Contenu scrollable */}
      <div className="flex-1 overflow-auto p-2">
        {renderView()}
      </div>
    </div>
  );
};

export default DocumentsWidget;
